import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Eyebrow, Empty } from "@/components/admin/ui";
import { Check, Clock, Copy, Loader2, MessageSquare, Send, Wallet, X } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const WaMark = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.95 1.16-.17.2-.35.22-.65.07-.3-.15-1.13-.42-2.15-1.33-.79-.71-1.33-1.58-1.48-1.88-.15-.3-.02-.46.13-.61.15-.15.35-.42.52-.62.13-.15.2-.27.3-.47.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.46 0 1.46 1.06 2.86 1.21 3.06.15.2 2.1 3.2 5.08 4.37 2.98 1.16 3.32.98 3.92.93.6-.05 1.94-.79 2.21-1.55.27-.77.27-1.43.2-1.56-.08-.13-.28-.2-.58-.35zM12.04 21.5h-.01a9.42 9.42 0 01-4.8-1.31l-.34-.2-3.57.93.96-3.48-.23-.36a9.4 9.4 0 01-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.99 6.67 2.77a9.37 9.37 0 012.76 6.68c0 5.2-4.24 9.43-9.45 9.43zM20.5 3.49A11.32 11.32 0 0012.04 0C5.79 0 .71 5.08.71 11.33c0 2 .52 3.95 1.52 5.67L0 24l7.17-1.88a11.3 11.3 0 004.86 1.11h.01c6.25 0 11.33-5.08 11.33-11.33 0-3.03-1.18-5.87-3.32-8.01z" />
  </svg>
);

const STAGE = {
  opened: { label: "Opened checkout", cls: "bg-white/[0.06] text-white/50" },
  upi_opened: { label: "Opened UPI app", cls: "bg-sky-400/20 text-sky-200" },
  says_paid: { label: "Says paid", cls: "bg-amber-300 text-black font-semibold" },
  reserved: { label: "Seat reserved", cls: "bg-violet-400/25 text-violet-100" },
  wa_sent: { label: "Locked on WhatsApp", cls: "bg-[#25D366] text-black font-semibold" },
  family_sent: { label: "Family got link", cls: "bg-pink-400/90 text-black font-semibold" },
  abandoned: { label: "Dropped off", cls: "bg-white/[0.06] text-white/40" },
  paid: { label: "PAID", cls: "bg-emerald-400 text-black font-bold" },
};

const FILTERS = [
  { id: "all", label: "Everyone" },
  { id: "says_paid", label: "Says paid — verify" },
  { id: "reserved", label: "Reserved / pay later" },
  { id: "cold", label: "Tapped pay, vanished" },
  { id: "paid", label: "Paid" },
];

const fmtClock = (v) =>
  v ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

/** The first 30 minutes after they tap are the only warm ones. */
const hotLeft = (updated) => {
  if (!updated) return 0;
  return Math.max(0, 30 - Math.floor((Date.now() - new Date(updated).getTime()) / 60000));
};

export function PaymentsPanel({ roomName, classTitle }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [tick, setTick] = useState(0);
  const [slug, setSlug] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  const registerLink = `${window.location.origin}/r/${slug || roomName}`;
  const evergreenLink = `${window.location.origin}/r/live`;

  useEffect(() => {
    api
      .get(`/public/meetings/${roomName}`)
      .then(({ data }) => setSlug(data.slug || ""))
      .catch(() => {});
  }, [roomName]);

  const saveSlug = async () => {
    setSavingSlug(true);
    try {
      await api.patch(`/meetings/${roomName}/settings`, { slug });
      toast.success(slug ? `Link name saved — /r/${slug}` : "Link name cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSlug(false);
    }
  };

  const load = () =>
    api
      .get(`/meetings/${roomName}/payments`)
      .then(({ data }) => {
        setRows(data.payments || []);
        setSummary(data.summary || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(() => {
      load();
      setTick((v) => v + 1);
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);

  const shown = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "paid") return rows.filter((r) => r.paid);
    if (filter === "says_paid") return rows.filter((r) => !r.paid && r.stage === "says_paid");
    if (filter === "reserved")
      return rows.filter((r) => !r.paid && ["reserved", "wa_sent", "family_sent"].includes(r.stage));
    return rows.filter((r) => !r.paid && ["opened", "upi_opened", "abandoned"].includes(r.stage));
  }, [rows, filter, tick]);

  const verify = async (r, paid) => {
    setBusy(r.id);
    try {
      await api.post(`/payments/${r.id}/verify`, { paid });
      toast.success(paid ? `${r.user_name} marked PAID` : `${r.user_name} moved back to unverified`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  // A message that reads like the host wrote it — name, seat, their own reason.
  const personalMsg = (r) => {
    const first = (r.user_name || "").split(" ")[0];
    const bits = [`Hi ${first}, this is from Udyog Classes 🙏`];
    if (r.paid) bits.push(`Your payment of ₹${r.amount} is confirmed — welcome aboard!`);
    else if (r.stage === "says_paid")
      bits.push(`We've received your ₹${r.amount} payment message — checking it now.`);
    else if (["reserved", "wa_sent", "family_sent"].includes(r.stage))
      bits.push(
        `Seat #${r.seat_no} is still held for you (code ${r.code}).${
          r.reason_label ? ` You mentioned: ${r.reason_label}.` : ""
        }${r.promised_at ? ` You said you'd pay ${r.promised_at.toLowerCase()}.` : ""}`
      );
    else
      bits.push(
        `I saw you opened the fee page during today's class but couldn't finish. Seat #${r.seat_no} is still yours — anything I can clear up?`
      );
    bits.push(`Amount: ₹${r.amount} (this exact amount is tagged to your seat)`);
    return bits.join("\n");
  };

  const pushPrivateCard = async (r) => {
    const first = (r.user_name || "").split(" ")[0];
    setBusy(r.id);
    try {
      await api.post(`/meetings/${roomName}/broadcast`, {
        text: `${first}, seat #${r.seat_no} is held for you — code ${r.code}. Tap below when you're ready.`,
        pay_button: true,
        to_user_name: r.user_name,
        duration_sec: 60,
      });
      toast.success(`Private card sent to ${r.user_name} inside the class`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading)
    return (
      <Card className="p-10 grid place-items-center" testid="report-payments">
        <Loader2 className="w-5 h-5 animate-spin text-white/30" />
      </Card>
    );

  return (
    <Card className="p-5" testid="report-payments">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <Eyebrow>Fees & seats</Eyebrow>
          <div className="text-[11px] text-white/35 mt-1" data-testid="pay-summary">
            {summary?.paid || 0} paid · {summary?.says_paid || 0} waiting for your check ·{" "}
            {summary?.reserved || 0} reserved · {summary?.abandoned || 0} dropped off ·{" "}
            <span className="text-emerald-300">₹{summary?.collected || 0} collected</span>
          </div>
        </div>
        <button
          onClick={async () => {
            const ok = await copyText(shown.map((r) => r.whatsapp).filter(Boolean).join("\n"));
            toast[ok ? "success" : "error"](ok ? `${shown.length} numbers copied` : "Couldn't copy");
          }}
          className="h-9 px-3.5 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/60 hover:text-white inline-flex items-center gap-2"
          data-testid="pay-copy-numbers"
        >
          <Copy className="w-3.5 h-3.5" /> Copy {shown.length} numbers
        </button>
      </div>

      {/* One link for every template, forever — always the live class. */}
      <div
        className="mb-3 px-4 py-3 rounded-2xl bg-emerald-400/[0.07] ring-1 ring-emerald-400/30"
        data-testid="pay-evergreen-row"
      >
        <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-300/90">
          Permanent link — paste once in your templates, it always opens today's class
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code
            className="min-w-0 flex-1 text-[13px] text-white font-semibold truncate"
            data-testid="pay-evergreen-link"
          >
            {evergreenLink}
          </code>
          <button
            onClick={async () => {
              const ok = await copyText(evergreenLink);
              toast[ok ? "success" : "error"](ok ? "Permanent link copied" : "Couldn't copy");
            }}
            className="h-9 px-3.5 rounded-full bg-emerald-400 hover:bg-emerald-300 text-black text-[10.5px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-2"
            data-testid="pay-copy-evergreen-link"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <a
            href={evergreenLink}
            target="_blank"
            rel="noreferrer"
            className="h-9 px-3.5 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/60 hover:text-white inline-flex items-center gap-2"
            data-testid="pay-open-evergreen-link"
          >
            Preview
          </a>
        </div>
        <div className="mt-1.5 text-[11.5px] text-white/35">
          Never needs changing when you create a new class — it points at whichever class is
          running, or the next scheduled one.
        </div>
      </div>

      {/* The public link to paste on any "Register now" button. */}
      <div
        className="mb-4 px-4 py-3 rounded-2xl bg-yellow-300/[0.07] ring-1 ring-yellow-300/25"
        data-testid="pay-register-link-row"
      >
        <div className="text-[10px] uppercase tracking-[0.14em] text-yellow-200/80">
          Registration link for THIS class only — put this on your “Register now” button
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-white/35 shrink-0">{window.location.origin}/r/</span>
          <input
            value={slug}
            onChange={(e) =>
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40))
            }
            placeholder={roomName}
            className="h-9 px-3 min-w-[180px] flex-1 rounded-lg bg-black/50 ring-1 ring-white/10 focus:ring-yellow-300/60 text-[12.5px] text-white outline-none transition-all"
            data-testid="pay-slug-input"
          />
          <button
            onClick={saveSlug}
            disabled={savingSlug}
            className="h-9 px-3.5 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/60 hover:text-white disabled:opacity-40"
            data-testid="pay-slug-save"
          >
            {savingSlug ? "Saving…" : "Save name"}
          </button>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 text-[12px] text-white/70 truncate" data-testid="pay-register-link">
            {registerLink}
          </code>
          <button
            onClick={async () => {
              const ok = await copyText(registerLink);
              toast[ok ? "success" : "error"](ok ? "Registration link copied" : "Couldn't copy");
            }}
            className="h-9 px-3.5 rounded-full bg-yellow-300 hover:bg-yellow-200 text-black text-[10.5px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-2"
            data-testid="pay-copy-register-link"
          >
            <Copy className="w-3.5 h-3.5" /> Copy link
          </button>
          <a
            href={registerLink}
            target="_blank"
            rel="noreferrer"
            className="h-9 px-3.5 rounded-full ring-1 ring-white/[0.12] text-[10.5px] uppercase tracking-[0.12em] text-white/60 hover:text-white inline-flex items-center gap-2"
            data-testid="pay-open-register-link"
          >
            Preview
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="pay-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`pay-filter-${f.id}`}
            className={`h-7 px-3 rounded-full text-[10px] uppercase tracking-[0.1em] transition-colors ${
              filter === f.id
                ? "bg-white text-black font-semibold"
                : "text-white/40 ring-1 ring-white/[0.07] hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <Empty
          icon={Wallet}
          text="Nobody has opened the fees checkout in this class yet. Send a broadcast with the “Pay Your Fees” button attached."
          testid="pay-empty"
        />
      ) : shown.length === 0 ? (
        <div className="py-10 text-center text-[12.5px] text-white/35" data-testid="pay-no-match">
          Nobody in this filter.
        </div>
      ) : (
        <div className="space-y-2" data-testid="pay-list">
          {shown.map((r) => {
            const st = STAGE[r.paid ? "paid" : r.stage] || STAGE.opened;
            const hot = !r.paid && hotLeft(r.updated_at);
            return (
              <div
                key={r.id}
                className="px-4 py-3 rounded-2xl bg-white/[0.025] ring-1 ring-white/[0.06] hover:ring-white/15 transition-colors"
                data-testid={`pay-row-${r.id}`}
              >
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="text-[10.5px] text-white/25 tabular-nums w-9">#{r.seat_no}</span>
                  <span className="text-[13.5px] text-white/90 truncate max-w-[150px]">
                    {r.user_name}
                  </span>
                  {r.whatsapp ? (
                    <a
                      href={`https://wa.me/91${r.whatsapp}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12.5px] text-white font-medium tabular-nums hover:text-[#25D366]"
                    >
                      +91 {r.whatsapp}
                    </a>
                  ) : (
                    <span className="text-[11px] text-white/25">no number</span>
                  )}
                  <span className="px-2 py-0.5 rounded-md bg-yellow-300 text-black text-[11px] font-bold tabular-nums">
                    ₹{r.amount}
                  </span>
                  {r.kind === "token" && (
                    <span className="px-1.5 py-0.5 rounded-md bg-violet-400/25 text-violet-100 text-[10px]">
                      booking
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded-md text-[10.5px] ${st.cls}`}>{st.label}</span>
                  {hot > 0 && (
                    <span className="px-2 py-0.5 rounded-md bg-red-500/20 text-red-200 text-[10px] inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> hot {hot}m
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-1.5">
                    {r.screenshot_url && (
                      <a
                        href={r.screenshot_url}
                        target="_blank"
                        rel="noreferrer"
                        className="h-8 w-8 grid place-items-center rounded-full ring-1 ring-emerald-400/40 overflow-hidden hover:ring-emerald-300"
                        data-testid={`pay-shot-${r.id}`}
                        title="View the payment screenshot they uploaded"
                      >
                        <img src={r.screenshot_url} alt="" className="w-full h-full object-cover" />
                      </a>
                    )}
                    {r.whatsapp && (
                      <a
                        href={`https://wa.me/91${r.whatsapp}?text=${encodeURIComponent(personalMsg(r))}`}
                        target="_blank"
                        rel="noreferrer"
                        className="h-8 px-3 grid place-items-center rounded-full bg-[#25D366] text-black text-[9.5px] uppercase tracking-[0.1em] font-bold hover:brightness-110"
                        data-testid={`pay-msg-${r.id}`}
                        title="Send a personal message with their name, seat and reason"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <WaMark size={13} /> Message
                        </span>
                      </a>
                    )}
                    <button
                      onClick={() => pushPrivateCard(r)}
                      disabled={busy === r.id}
                      className="h-8 px-3 rounded-full ring-1 ring-white/[0.12] text-[9.5px] uppercase tracking-[0.1em] text-white/55 hover:text-white hover:ring-white/30 disabled:opacity-40 inline-flex items-center gap-1.5"
                      data-testid={`pay-card-${r.id}`}
                      title="Pop a private card on this student's screen, in class"
                    >
                      <Send className="w-3 h-3" /> In-class card
                    </button>
                    {r.paid ? (
                      <button
                        onClick={() => verify(r, false)}
                        disabled={busy === r.id}
                        className="h-8 w-8 grid place-items-center rounded-full text-white/30 hover:text-red-300 disabled:opacity-40"
                        data-testid={`pay-unverify-${r.id}`}
                        title="Undo paid"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => verify(r, true)}
                        disabled={busy === r.id}
                        className="h-8 px-3 rounded-full bg-emerald-400 text-black text-[9.5px] uppercase tracking-[0.1em] font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5"
                        data-testid={`pay-verify-${r.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Mark paid
                      </button>
                    )}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mt-2 pl-9 text-[10.5px] text-white/40">
                  <span className="px-1.5 py-0.5 rounded bg-white/[0.05]">{r.code}</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/[0.05]">{r.minutes || 0}m in class</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/[0.05]">
                    opened {fmtClock(r.created_at)} · last action {fmtClock(r.updated_at)}
                  </span>
                  {r.reason_label && (
                    <span className="px-1.5 py-0.5 rounded bg-violet-400/20 text-violet-100">
                      {r.reason_label}
                    </span>
                  )}
                  {r.promised_at && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-300/20 text-amber-100">
                      will pay {r.promised_at.toLowerCase()}
                    </span>
                  )}
                  {r.family_number && (
                    <span className="px-1.5 py-0.5 rounded bg-pink-400/20 text-pink-100 inline-flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> family +91 {r.family_number}
                    </span>
                  )}
                  {r.verified_by && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-200">
                      verified by {r.verified_by}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
